/**
 * D-EZ-102 — WHERE A DRIVER IS FROM.
 *
 * A broadcast introduces a man by his hometown ("the Mooresville driver"), and
 * a generated field has none. This gives every FICTIONAL driver one: a real
 * small town in stock-car country, chosen by a hash of his driver id. No die
 * is thrown — the season's dice stream is untouched — and the same id always
 * gets the same town, across saves, reloads and every screen.
 *
 * ⚠ NOT FOR REAL DRIVERS. The 2025 scenario's cards (`rw25-…`) are real men
 * with real hometowns this repo does not hold; inventing one for Denny Hamlin
 * would be printing a falsehood about a real person. They get `null`, and a
 * screen simply leaves the line out.
 */

/** Small towns in the Southeast, Appalachia and the Midwest — racing country. */
export const HOMETOWNS: readonly string[] = [
  'Mooresville, N.C.',
  'Hickory, N.C.',
  'Concord, N.C.',
  'Kannapolis, N.C.',
  'Wilkesboro, N.C.',
  'Level Cross, N.C.',
  'Randleman, N.C.',
  'Thomasville, N.C.',
  'Denver, N.C.',
  'Lincolnton, N.C.',
  'Shelby, N.C.',
  'Statesville, N.C.',
  'Mount Airy, N.C.',
  'Boone, N.C.',
  'Rockingham, N.C.',
  'Hendersonville, N.C.',
  'Gaffney, S.C.',
  'Darlington, S.C.',
  'Florence, S.C.',
  'Spartanburg, S.C.',
  'Anderson, S.C.',
  'Greer, S.C.',
  'Hartsville, S.C.',
  'Dawsonville, Ga.',
  'Cartersville, Ga.',
  'Dalton, Ga.',
  'Gainesville, Ga.',
  'Toccoa, Ga.',
  'Calhoun, Ga.',
  'Hueytown, Ala.',
  'Talladega, Ala.',
  'Anniston, Ala.',
  'Cullman, Ala.',
  'Fort Payne, Ala.',
  'Oxford, Ala.',
  'Bristol, Tenn.',
  'Kingsport, Tenn.',
  'Johnson City, Tenn.',
  'Morristown, Tenn.',
  'Cleveland, Tenn.',
  'Shelbyville, Tenn.',
  'Lebanon, Tenn.',
  'Owensboro, Ky.',
  'Bowling Green, Ky.',
  'Corbin, Ky.',
  'Paducah, Ky.',
  'Hazard, Ky.',
  'Martinsville, Va.',
  'Ridgeway, Va.',
  'South Boston, Va.',
  'Danville, Va.',
  'Galax, Va.',
  'Wytheville, Va.',
  'Abingdon, Va.',
  'Bluefield, W.Va.',
  'Beckley, W.Va.',
  'Princeton, W.Va.',
  'Elkins, W.Va.',
  'Easley, S.C.',
  'Pensacola, Fla.',
  'Lake City, Fla.',
  'Live Oak, Fla.',
  'DeFuniak Springs, Fla.',
  'Hattiesburg, Miss.',
  'Tupelo, Miss.',
  'Laurel, Miss.',
  'Ruston, La.',
  'Monroe, La.',
  'Texarkana, Ark.',
  'Batesville, Ark.',
  'Joplin, Mo.',
  'Sedalia, Mo.',
  'Springfield, Mo.',
  'Hannibal, Mo.',
  'Salem, Ind.',
  'Columbus, Ind.',
  'Kokomo, Ind.',
  'Anderson, Ind.',
  'Winchester, Ind.',
  'Terre Haute, Ind.',
  'Findlay, Ohio',
  'Chillicothe, Ohio',
  'Sandusky, Ohio',
  'Marion, Ohio',
  'Zanesville, Ohio',
  'Effingham, Ill.',
  'Danville, Ill.',
  'Galesburg, Ill.',
  'Quincy, Ill.',
  'Knoxville, Iowa',
  'Oskaloosa, Iowa',
  'Boone, Iowa',
  'Jackson, Mich.',
  'Cadillac, Mich.',
  'Alpena, Mich.',
  'Eau Claire, Wis.',
  'Wausau, Wis.',
  'Slinger, Wis.',
  'Concordia, Kan.',
  'Hutchinson, Kan.',
  'Emporia, Kan.',
];

/** The 2025 scenario's real drivers — never given an invented hometown. */
export function isRealDriver(driverId: string): boolean {
  return driverId.startsWith('rw25-');
}

/** FNV-1a, 32-bit. Stable, cheap, and not a die. */
export function stableHash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** A fictional driver's hometown; `null` for a real one. */
export function hometownFor(driverId: string): string | null {
  if (!driverId || isRealDriver(driverId)) return null;
  return HOMETOWNS[stableHash(`hometown|${driverId}`) % HOMETOWNS.length];
}
